import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Grid, Tabs, Tab,
  Chip, Alert, CircularProgress, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { Assessment, Print, Refresh, PictureAsPdf, TableChart } from "@mui/icons-material";
import axios from "axios";
import { exportarPDF, exportarExcel } from "../../utils/exportar";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function Reportes() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().split("T")[0]);
  const [fechaHasta, setFechaHasta] = useState(new Date().toISOString().split("T")[0]);
  const [balance, setBalance] = useState(null);
  const [resultados, setResultados] = useState(null);
  const [comprobacion, setComprobacion] = useState(null);
  const [mayor, setMayor] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState("");
  const [reporteCxP, setReporteCxP] = useState(null);
  const [reporteCxC, setReporteCxC] = useState(null);
  const [reporteNC, setReporteNC] = useState(null);
  const [reporteLC, setReporteLC] = useState(null);
  const [reporteAsientos, setReporteAsientos] = useState(null);
  const [reporteTrib, setReporteTrib] = useState(null);
  const [validacionPlan, setValidacionPlan] = useState(null);
  const [mostrarTodasCuentas, setMostrarTodasCuentas] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const [sincMsg, setSincMsg] = useState("");

  useEffect(() => { cargarCuentas(); }, []);

  const cargarCuentas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/contabilidad/plan-cuentas`, { headers: headers() });
      setCuentas(res.data.filter(c => c.acepta_movimiento));
    } catch {}
  };

  const cargarBalance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_hasta', fechaHasta);
      if (mostrarTodasCuentas) params.append('incluir_sin_movimiento', 'true');
      
      const res = await axios.get(`${API_URL}/api/reportes/balance-general?${params}`, { headers: headers() });
      setBalance(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarResultados = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/estado-resultados?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setResultados(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarComprobacion = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_hasta', fechaHasta);
      if (mostrarTodasCuentas) params.append('incluir_todas', 'true');
      
      const res = await axios.get(`${API_URL}/api/reportes/balance-comprobacion?${params}`, { headers: headers() });
      setComprobacion(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarMayor = async () => {
    if (!cuentaSeleccionada) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/mayor-general?cuenta_id=${cuentaSeleccionada}&fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setMayor(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarCxP = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/cuentas-pagar?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setReporteCxP(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarCxC = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/cuentas-cobrar?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setReporteCxC(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarNC = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/notas-credito?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setReporteNC(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarLC = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/liquidaciones-compras?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setReporteLC(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarAsientos = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('fecha_desde', fechaDesde);
      params.append('fecha_hasta', fechaHasta);
      
      const res = await axios.get(`${API_URL}/api/contabilidad/asientos?${params}`, { headers: headers() });
      setReporteAsientos(res.data);
    } catch {} finally { setLoading(false); }
  };

  const cargarTributario = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reportes/resumen-tributario?fecha_desde=${fechaDesde}&fecha_hasta=${fechaHasta}`, { headers: headers() });
      setReporteTrib(res.data);
    } catch {} finally { setLoading(false); }
  };

  const handleTabChange = (_, v) => { setTab(v); };

  const sincronizarContabilidad = async () => {
    setLoading(true); setSincMsg("");
    try {
      // Primero actualizar plan de cuentas
      await axios.post(`${API_URL}/api/sincronizar-contabilidad/actualizar-plan-cuentas`, {}, { headers: headers() });
      // Luego sincronizar asientos
      const res = await axios.post(`${API_URL}/api/sincronizar-contabilidad/todo`, {}, { headers: headers() });
      setSincMsg(`✅ ${res.data.asientos_generados} asientos generados. ${res.data.errores > 0 ? `⚠️ ${res.data.errores} errores.` : "Sin errores."}`);
    } catch (e) { setSincMsg("❌ Error: " + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const validarPlanCuentas = async () => {
    setLoading(true); setSincMsg("");
    try {
      const res = await axios.get(`${API_URL}/api/reportes/validar-plan-cuentas`, { headers: headers() });
      setValidacionPlan(res.data);
      setSincMsg(`🔍 Validación completada: ${res.data.analisis.total_cuentas} cuentas, ${res.data.analisis.cuentas_sin_movimientos} sin movimientos`);
    } catch (e) { setSincMsg("❌ Error validando: " + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const repararHuerfanas = async () => {
    if (!window.confirm("¿Reparar cuentas huérfanas? Esto reasignará los asientos con IDs inválidos a las cuentas correctas del plan actual.")) return;
    setLoading(true); setSincMsg("");
    try {
      const res = await axios.post(`${API_URL}/api/reportes/reparar-cuentas-huerfanas`, {}, { headers: headers() });
      setSincMsg(`✅ ${res.data.message}`);
      // Revalidar después de reparar
      validarPlanCuentas();
    } catch (e) { setSincMsg("❌ Error reparando: " + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const validarAsientosFacturas = async () => {
    if (!window.confirm("¿Validar asientos contra facturas y liquidaciones? Se reversarán los asientos de documentos anulados o que no existen (NO afecta compras).")) return;
    setLoading(true); setSincMsg("");
    try {
      const res = await axios.post(`${API_URL}/api/reportes/validar-asientos-facturas`, {}, { headers: headers() });
      const data = res.data;
      setSincMsg(`✅ ${data.message} | Facturas autorizadas: ${data.facturas_autorizadas} | Liquidaciones autorizadas: ${data.liquidaciones_autorizadas}`);
    } catch (e) { setSincMsg("❌ Error validando asientos: " + (e.response?.data?.error || e.message)); }
    setLoading(false);
  };

  const fmt = (n) => parseFloat(n || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Helper para formatear fecha YYYY-MM-DD -> DD/MM/YYYY
  const fmtFecha = (fecha) => {
    if (!fecha) return "";
    const parts = String(fecha).split("T")[0].split("-");
    if (parts.length !== 3) return fecha;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // ==================== EXPORTACIONES ====================
  const periodoFmt = `Del ${fmtFecha(fechaDesde)} al ${fmtFecha(fechaHasta)}`;
  const fechaGeneracion = new Date().toLocaleDateString("es-EC", { day: "2-digit", month: "long", year: "numeric" });

  const exportBalanceGeneral = (tipo) => {
    if (!balance) return;
    const cols = ["Código", "Cuenta", "Saldo"];
    const filas = [
      ...balance.activos.map(c => [c.codigo, c.nombre, c.saldo]),
      ["", "TOTAL ACTIVOS", balance.totalActivos],
      [],
      ...balance.pasivos.map(c => [c.codigo, c.nombre, c.saldo]),
      ["", "TOTAL PASIVOS", balance.totalPasivos],
      [],
      ...balance.patrimonio.map(c => [c.codigo, c.nombre, c.saldo]),
      ["", "TOTAL PATRIMONIO", balance.totalPatrimonio],
    ];
    const resumen = [
      { label: "Total Activos", valor: `$${balance.totalActivos}` },
      { label: "Total Pasivos", valor: `$${balance.totalPasivos}` },
      { label: "Total Patrimonio", valor: `$${balance.totalPatrimonio}` },
      { label: "Cuadre (A - P - Pt)", valor: `$${balance.cuadre}` },
    ];
    if (tipo === "pdf") exportarPDF("Balance_General", "BALANCE GENERAL", cols, filas, { subtitulo: `Al ${fmtFecha(fechaHasta)} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Balance_General", "Balance General", cols, filas, resumen);
  };

  const exportEstadoResultados = (tipo) => {
    if (!resultados) return;
    const cols = ["Código", "Cuenta", "Valor"];
    const filas = [
      ["", "--- INGRESOS ---", ""],
      ...resultados.ingresos.map(c => [c.codigo, c.nombre, c.saldo]),
      ["", "Total Ingresos", resultados.totalIngresos],
      ["", "--- COSTOS ---", ""],
      ...resultados.costos.map(c => [c.codigo, c.nombre, c.saldo]),
      ["", "Total Costos", resultados.totalCostos],
      ["", "--- GASTOS ---", ""],
      ...resultados.gastos.map(c => [c.codigo, c.nombre, c.saldo]),
      ["", "Total Gastos", resultados.totalGastos],
    ];
    const resumen = [
      { label: "Utilidad Bruta", valor: `$${resultados.utilidadBruta}` },
      { label: "UTILIDAD NETA", valor: `$${resultados.utilidadNeta}` },
    ];
    if (tipo === "pdf") exportarPDF("Estado_Resultados", "ESTADO DE RESULTADOS", cols, filas, { subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Estado_Resultados", "Estado Resultados", cols, filas, resumen);
  };

  const exportComprobacion = (tipo) => {
    if (!comprobacion) return;
    const cols = ["Código", "Cuenta", "Debe", "Haber", "Saldo Deudor", "Saldo Acreedor"];
    const filas = comprobacion.cuentas.map(c => [c.codigo, c.nombre, c.debe, c.haber, c.saldoDeudor, c.saldoAcreedor]);
    const resumen = [
      { label: "Total Debe", valor: `$${comprobacion.totalDebe}` },
      { label: "Total Haber", valor: `$${comprobacion.totalHaber}` },
      { label: "Cuadre", valor: `$${comprobacion.cuadre}` },
    ];
    if (tipo === "pdf") exportarPDF("Balance_Comprobacion", "BALANCE DE COMPROBACIÓN", cols, filas, { orientacion: "landscape", subtitulo: `Al ${fmtFecha(fechaHasta)} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Balance_Comprobacion", "Comprobación", cols, filas, resumen);
  };

  const exportMayor = (tipo) => {
    if (!mayor) return;
    const cols = ["Fecha", "Detalle", "Debe", "Haber", "Saldo"];
    const filas = mayor.movimientos.map(m => [m.fecha?.split("T")[0] || m.fecha, m.glosa, m.debe, m.haber, m.saldo]);
    const resumen = [{ label: "Saldo Final", valor: `$${mayor.saldoFinal}` }];
    if (tipo === "pdf") exportarPDF(`Mayor_${mayor.cuenta.codigo}`, `MAYOR GENERAL - ${mayor.cuenta.codigo} ${mayor.cuenta.nombre}`, cols, filas, { subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel(`Mayor_${mayor.cuenta.codigo}`, "Mayor General", cols, filas, resumen);
  };

  const exportCxP = (tipo) => {
    if (!reporteCxP) return;
    const cols = ["Proveedor", "RUC", "Factura", "Fecha", "Total", "Pagado", "Saldo", "Estado"];
    const filas = reporteCxP.detalle.map(d => [d.proveedor, d.ruc, d.numero, d.fecha?.split("T")[0], d.total, d.pagado, d.saldo, d.estado]);
    const resumen = [
      { label: "Total Comprado", valor: `$${reporteCxP.resumen.total_comprado}` },
      { label: "Total Pagado", valor: `$${reporteCxP.resumen.total_pagado}` },
      { label: "Total Pendiente", valor: `$${reporteCxP.resumen.total_pendiente}` },
    ];
    if (tipo === "pdf") exportarPDF("Cuentas_por_Pagar", "REPORTE CUENTAS POR PAGAR", cols, filas, { orientacion: "landscape", subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Cuentas_por_Pagar", "CxP", cols, filas, resumen);
  };

  const exportCxC = (tipo) => {
    if (!reporteCxC) return;
    const cols = ["Cliente", "RUC", "Secuencial", "Fecha", "Total", "Cobrado", "Saldo", "Estado"];
    const filas = reporteCxC.detalle.map(d => [d.cliente, d.ruc, d.secuencial, d.fecha, d.total, d.cobrado, d.saldo, d.estado]);
    const resumen = [
      { label: "Total Facturado", valor: `$${reporteCxC.resumen.total_facturado}` },
      { label: "Total Cobrado", valor: `$${reporteCxC.resumen.total_cobrado}` },
      { label: "Total Pendiente", valor: `$${reporteCxC.resumen.total_pendiente}` },
    ];
    if (tipo === "pdf") exportarPDF("Cuentas_por_Cobrar", "REPORTE CUENTAS POR COBRAR", cols, filas, { orientacion: "landscape", subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Cuentas_por_Cobrar", "CxC", cols, filas, resumen);
  };

  const exportNC = (tipo) => {
    if (!reporteNC) return;
    const cols = ["Número", "Fecha", "Cliente", "RUC", "Factura Orig.", "Motivo", "Total"];
    const filas = reporteNC.detalle.map(d => [d.numero, fmtFecha(d.fecha), d.cliente, d.ruc, d.factura_original, d.motivo, `$${fmt(d.total)}`]);
    const resumen = [
      { label: "Total Notas de Crédito (Autorizadas)", valor: `$${fmt(reporteNC.resumen.total)}` },
      { label: "Cantidad", valor: String(reporteNC.resumen.cantidad) },
    ];
    if (tipo === "pdf") exportarPDF("Notas_Credito", "REPORTE NOTAS DE CRÉDITO (AUTORIZADAS)", cols, filas, { orientacion: "landscape", subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Notas_Credito", "Notas Crédito", cols, filas, resumen);
  };

  const exportLC = (tipo) => {
    if (!reporteLC) return;
    const cols = ["Número", "Fecha", "Proveedor", "RUC", "Total", "Retenido", "A Pagar"];
    const filas = reporteLC.detalle.map(d => [d.numero, fmtFecha(d.fecha), d.proveedor, d.ruc, `$${fmt(d.total)}`, `$${fmt(d.retenido)}`, `$${fmt(d.a_pagar)}`]);
    const resumen = [
      { label: "Total Liquidaciones (Autorizadas)", valor: `$${fmt(reporteLC.resumen.total)}` },
      { label: "Total Retenido", valor: `$${fmt(reporteLC.resumen.total_retenido)}` },
      { label: "Total Pagado", valor: `$${fmt(reporteLC.resumen.total_pagado)}` },
      { label: "Cantidad", valor: String(reporteLC.resumen.cantidad) },
    ];
    if (tipo === "pdf") exportarPDF("Liquidaciones_Compras", "REPORTE LIQUIDACIONES DE COMPRAS (AUTORIZADAS)", cols, filas, { orientacion: "landscape", subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}`, resumen });
    else exportarExcel("Liquidaciones_Compras", "Liquidaciones", cols, filas, resumen);
  };

  const exportAsientos = (tipo) => {
    if (!reporteAsientos) return;
    
    // Preparar datos para exportar
    const datosExport = [];
    reporteAsientos.forEach(asiento => {
      if (asiento.detalles && asiento.detalles.length > 0) {
        asiento.detalles.forEach(detalle => {
          const cuenta = cuentas.find(c => c.id === detalle.cuenta_id);
          datosExport.push([
            new Date(asiento.fecha_transaccion).toLocaleDateString(),
            asiento.glosa,
            asiento.num_comprobante_sri || '',
            asiento.tipo_comprobante_sri || '',
            cuenta?.codigo || '',
            cuenta?.nombre || '',
            detalle.ruc_tercero || '',
            parseFloat(detalle.debe || 0).toFixed(2),
            parseFloat(detalle.haber || 0).toFixed(2),
            asiento.estado
          ]);
        });
      }
    });

    const cols = ["Fecha", "Glosa", "Comprobante", "Tipo", "Cód. Cuenta", "Nombre Cuenta", "RUC Tercero", "Debe", "Haber", "Estado"];
    
    if (tipo === "pdf") {
      exportarPDF("Asientos_Contables", "REPORTE DE ASIENTOS CONTABLES", cols, datosExport, { 
        orientacion: "landscape", 
        subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}` 
      });
    } else {
      exportarExcel("Asientos_Contables", "Asientos Contables", cols, datosExport);
    }
  };

  const exportTributario = (tipo) => {
    if (!reporteTrib) return;
    const cols = ["Concepto", "Valor"];
    const filas = [
      ["IVA en Ventas (cobrado)", `$${fmt(reporteTrib.iva_ventas)}`],
      ["IVA en Compras (pagado)", `$${fmt(reporteTrib.iva_compras)}`],
      ["IVA a Pagar / Crédito Tributario", `$${fmt(reporteTrib.iva_a_pagar)}`],
      ["", ""],
      ["Retenciones Fuente por Pagar (IR)", `$${fmt(reporteTrib.retenciones_fuente_por_pagar)}`],
      ["Retenciones IVA por Pagar", `$${fmt(reporteTrib.retenciones_iva_por_pagar)}`],
      ["Crédito Tributario Renta", `$${fmt(reporteTrib.credito_tributario_renta)}`],
      ["", ""],
      ["TOTAL OBLIGACIONES SRI", `$${fmt(reporteTrib.total_obligaciones_sri)}`],
    ];
    if (tipo === "pdf") exportarPDF("Resumen_Tributario", "RESUMEN TRIBUTARIO", cols, filas, { subtitulo: `${periodoFmt} | Generado: ${fechaGeneracion}` });
    else exportarExcel("Resumen_Tributario", "Tributario", cols, filas);
  };

  // Componente reutilizable de botones de exportación
  const BotonesExportar = ({ onPDF, onExcel, disabled }) => (
    <Box sx={{ display: "inline-flex", gap: 1, ml: 1 }}>
      <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdf />} onClick={onPDF} disabled={disabled}>PDF</Button>
      <Button size="small" variant="outlined" color="success" startIcon={<TableChart />} onClick={onExcel} disabled={disabled}>Excel</Button>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Assessment color="primary" />
          <Typography variant="h5" fontWeight={700}>Reportes Contables</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" type="date" label="Desde" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField fullWidth size="small" type="date" label="Hasta" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Mostrar Cuentas</InputLabel>
              <Select
                value={mostrarTodasCuentas}
                label="Mostrar Cuentas"
                onChange={(e) => setMostrarTodasCuentas(e.target.value)}
              >
                <MenuItem value={false}>Solo con movimientos</MenuItem>
                <MenuItem value={true}>Todas las cuentas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Información de validación del plan de cuentas */}
        {validacionPlan && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>📊 Estado del Plan de Cuentas:</Typography>
            <Grid container spacing={2} sx={{ fontSize: '0.875rem' }}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Total:</strong> {validacionPlan.analisis.total_cuentas} cuentas
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Activas:</strong> {validacionPlan.analisis.cuentas_activas}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Con movimientos:</strong> {validacionPlan.analisis.cuentas_con_movimientos}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2">
                  <strong>Sin movimientos:</strong> {validacionPlan.analisis.cuentas_sin_movimientos}
                </Typography>
              </Grid>
            </Grid>
            {validacionPlan.recomendaciones.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="warning.main">
                  ⚠️ {validacionPlan.recomendaciones.join('. ')}
                </Typography>
              </Box>
            )}
            {validacionPlan.analisis.cuentas_huerfanas > 0 && (
              <Box sx={{ mt: 1 }}>
                <Button 
                  variant="contained" 
                  color="error" 
                  size="small"
                  disabled={loading}
                  onClick={repararHuerfanas}
                >
                  🔧 Reparar {validacionPlan.analisis.cuentas_huerfanas} cuentas huérfanas
                </Button>
              </Box>
            )}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                color="info" 
                onClick={validarPlanCuentas} 
                disabled={loading} 
                size="small"
              >
                🔍 Validar Plan
              </Button>
              <Button 
                variant="outlined" 
                color="secondary" 
                onClick={validarAsientosFacturas} 
                disabled={loading} 
                size="small"
              >
                🧹 Validar Asientos
              </Button>
              <Button 
                variant="contained" 
                color="warning" 
                onClick={sincronizarContabilidad} 
                disabled={loading} 
                size="small" 
                sx={{ whiteSpace: "nowrap" }}
              >
                🔄 Sincronizar Contabilidad
              </Button>
            </Box>
          }
        >
          {sincMsg || "Si los reportes salen vacíos o faltan cuentas, use 'Validar Plan' para diagnosticar y 'Sincronizar Contabilidad' para generar asientos faltantes."}
        </Alert>

        <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
          <Tab label="Balance General" />
          <Tab label="Estado de Resultados" />
          <Tab label="Balance Comprobación" />
          <Tab label="Mayor General" />
          <Tab label="Cuentas por Pagar" />
          <Tab label="Cuentas por Cobrar" />
          <Tab label="Notas de Crédito" />
          <Tab label="Liquidaciones" />
          <Tab label="Asientos Contables" />
          <Tab label="Resumen Tributario" />
        </Tabs>

        {loading && <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>}

        {/* Balance General */}
        {tab === 0 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarBalance} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar Balance</Button>
            <BotonesExportar onPDF={() => exportBalanceGeneral("pdf")} onExcel={() => exportBalanceGeneral("excel")} disabled={!balance} />
            {balance && (
              <Box>
                {/* Estadísticas del balance */}
                {balance.estadisticas && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>📈 Estadísticas del Balance:</Typography>
                    <Grid container spacing={2} sx={{ fontSize: '0.875rem' }}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Total cuentas: {balance.estadisticas.total_cuentas}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Con movimiento: {balance.estadisticas.cuentas_con_movimiento}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Sin movimiento: {balance.estadisticas.cuentas_sin_movimiento}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">
                          Mostradas: {balance.activos.length + balance.pasivos.length + balance.patrimonio.length}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Alert>
                )}
                
                <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" color="primary" gutterBottom>ACTIVOS</Typography>
                  <TableContainer><Table size="small">
                    <TableBody>
                      {balance.activos.map((c, i) => (
                        <TableRow key={i} sx={{ 
                          bgcolor: !c.tiene_movimientos ? '#f9f9f9' : 'inherit',
                          opacity: !c.tiene_movimientos ? 0.7 : 1
                        }}>
                          <TableCell>
                            {c.codigo}
                            {!c.tiene_movimientos && <Chip label="Sin mov." size="small" sx={{ ml: 1, fontSize: '0.6rem' }} />}
                          </TableCell>
                          <TableCell>{c.nombre}</TableCell>
                          <TableCell align="right">${fmt(c.saldo)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#e3f2fd" }}><TableCell colSpan={2}><strong>TOTAL ACTIVOS</strong></TableCell><TableCell align="right"><strong>${fmt(balance.totalActivos)}</strong></TableCell></TableRow>
                    </TableBody>
                  </Table></TableContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" color="error" gutterBottom>PASIVOS</Typography>
                  <TableContainer><Table size="small">
                    <TableBody>
                      {balance.pasivos.map((c, i) => (
                        <TableRow key={i} sx={{ 
                          bgcolor: !c.tiene_movimientos ? '#f9f9f9' : 'inherit',
                          opacity: !c.tiene_movimientos ? 0.7 : 1
                        }}>
                          <TableCell>
                            {c.codigo}
                            {!c.tiene_movimientos && <Chip label="Sin mov." size="small" sx={{ ml: 1, fontSize: '0.6rem' }} />}
                          </TableCell>
                          <TableCell>{c.nombre}</TableCell>
                          <TableCell align="right">${fmt(c.saldo)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#ffebee" }}><TableCell colSpan={2}><strong>TOTAL PASIVOS</strong></TableCell><TableCell align="right"><strong>${fmt(balance.totalPasivos)}</strong></TableCell></TableRow>
                    </TableBody>
                  </Table></TableContainer>
                  <Typography variant="h6" color="success.main" gutterBottom sx={{ mt: 2 }}>PATRIMONIO</Typography>
                  <TableContainer><Table size="small">
                    <TableBody>
                      {balance.patrimonio.map((c, i) => (
                        <TableRow key={i} sx={{ 
                          bgcolor: !c.tiene_movimientos ? '#f9f9f9' : 'inherit',
                          opacity: !c.tiene_movimientos ? 0.7 : 1
                        }}>
                          <TableCell>
                            {c.codigo}
                            {!c.tiene_movimientos && <Chip label="Sin mov." size="small" sx={{ ml: 1, fontSize: '0.6rem' }} />}
                          </TableCell>
                          <TableCell>{c.nombre}</TableCell>
                          <TableCell align="right">${fmt(c.saldo)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#e8f5e9" }}><TableCell colSpan={2}><strong>TOTAL PATRIMONIO</strong></TableCell><TableCell align="right"><strong>${fmt(balance.totalPatrimonio)}</strong></TableCell></TableRow>
                    </TableBody>
                  </Table></TableContainer>
                  <Alert severity={balance.cuadre === "0.00" ? "success" : "warning"} sx={{ mt: 2 }}>
                    Diferencia: ${fmt(balance.cuadre)} {balance.cuadre === "0.00" ? "✅ Cuadrado" : "⚠️ Descuadrado"}
                  </Alert>
                </Grid>
              </Grid>
              </Box>
            )}
          </Box>
        )}

        {/* Estado de Resultados */}
        {tab === 1 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarResultados} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar</Button>
            <BotonesExportar onPDF={() => exportEstadoResultados("pdf")} onExcel={() => exportEstadoResultados("excel")} disabled={!resultados} />
            {resultados && (
              <Box>
                <Typography variant="h6" color="success.main">INGRESOS</Typography>
                <TableContainer><Table size="small"><TableBody>
                  {resultados.ingresos.map((c, i) => (<TableRow key={i}><TableCell>{c.codigo}</TableCell><TableCell>{c.nombre}</TableCell><TableCell align="right">${fmt(c.saldo)}</TableCell></TableRow>))}
                  <TableRow sx={{ bgcolor: "#e8f5e9" }}><TableCell colSpan={2}><strong>Total Ingresos</strong></TableCell><TableCell align="right"><strong>${fmt(resultados.totalIngresos)}</strong></TableCell></TableRow>
                </TableBody></Table></TableContainer>

                <Typography variant="h6" color="warning.main" sx={{ mt: 2 }}>COSTOS</Typography>
                <TableContainer><Table size="small"><TableBody>
                  {resultados.costos.map((c, i) => (<TableRow key={i}><TableCell>{c.codigo}</TableCell><TableCell>{c.nombre}</TableCell><TableCell align="right">${fmt(c.saldo)}</TableCell></TableRow>))}
                  <TableRow sx={{ bgcolor: "#fff3e0" }}><TableCell colSpan={2}><strong>Total Costos</strong></TableCell><TableCell align="right"><strong>${fmt(resultados.totalCostos)}</strong></TableCell></TableRow>
                </TableBody></Table></TableContainer>

                <Alert severity="info" sx={{ mt: 1 }}>Utilidad Bruta: <strong>${fmt(resultados.utilidadBruta)}</strong></Alert>

                <Typography variant="h6" color="error" sx={{ mt: 2 }}>GASTOS</Typography>
                <TableContainer><Table size="small"><TableBody>
                  {resultados.gastos.map((c, i) => (<TableRow key={i}><TableCell>{c.codigo}</TableCell><TableCell>{c.nombre}</TableCell><TableCell align="right">${fmt(c.saldo)}</TableCell></TableRow>))}
                  <TableRow sx={{ bgcolor: "#ffebee" }}><TableCell colSpan={2}><strong>Total Gastos</strong></TableCell><TableCell align="right"><strong>${fmt(resultados.totalGastos)}</strong></TableCell></TableRow>
                </TableBody></Table></TableContainer>

                <Alert severity={parseFloat(resultados.utilidadNeta) >= 0 ? "success" : "error"} sx={{ mt: 2 }}>
                  <strong>UTILIDAD NETA: ${fmt(resultados.utilidadNeta)}</strong>
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {/* Balance de Comprobación */}
        {tab === 2 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarComprobacion} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar</Button>
            <BotonesExportar onPDF={() => exportComprobacion("pdf")} onExcel={() => exportComprobacion("excel")} disabled={!comprobacion} />
            {comprobacion && (
              <Box>
                {/* Estadísticas del balance de comprobación */}
                {comprobacion.estadisticas && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>📊 Estadísticas del Balance de Comprobación:</Typography>
                    <Grid container spacing={2} sx={{ fontSize: '0.875rem' }}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Plan total: {comprobacion.estadisticas.total_cuentas_plan}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Mostradas: {comprobacion.estadisticas.cuentas_mostradas}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Con movimiento: {comprobacion.estadisticas.cuentas_con_movimiento}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2">Sin movimiento: {comprobacion.estadisticas.cuentas_sin_movimiento}</Typography>
                      </Grid>
                    </Grid>
                  </Alert>
                )}
                
                <TableContainer><Table size="small">
                <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>Código</strong></TableCell><TableCell><strong>Cuenta</strong></TableCell>
                  <TableCell align="right"><strong>Debe</strong></TableCell><TableCell align="right"><strong>Haber</strong></TableCell>
                  <TableCell align="right"><strong>Saldo Deudor</strong></TableCell><TableCell align="right"><strong>Saldo Acreedor</strong></TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {comprobacion.cuentas.map((c, i) => (
                    <TableRow key={i} hover sx={{ 
                      bgcolor: !c.tiene_movimientos ? '#f9f9f9' : 'inherit',
                      opacity: !c.tiene_movimientos ? 0.7 : 1
                    }}>
                      <TableCell>
                        {c.codigo}
                        {!c.tiene_movimientos && <Chip label="Sin mov." size="small" sx={{ ml: 1, fontSize: '0.6rem' }} />}
                      </TableCell>
                      <TableCell>{c.nombre}</TableCell>
                      <TableCell align="right">${fmt(c.debe)}</TableCell><TableCell align="right">${fmt(c.haber)}</TableCell>
                      <TableCell align="right">{parseFloat(c.saldoDeudor) > 0 ? `$${fmt(c.saldoDeudor)}` : ""}</TableCell>
                      <TableCell align="right">{parseFloat(c.saldoAcreedor) > 0 ? `$${fmt(c.saldoAcreedor)}` : ""}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell colSpan={2}><strong>TOTALES</strong></TableCell>
                    <TableCell align="right"><strong>${fmt(comprobacion.totalDebe)}</strong></TableCell>
                    <TableCell align="right"><strong>${fmt(comprobacion.totalHaber)}</strong></TableCell>
                    <TableCell colSpan={2} align="center">
                      <Chip label={comprobacion.cuadre === "0.00" ? "✅ Cuadrado" : `⚠️ Dif: $${comprobacion.cuadre}`} color={comprobacion.cuadre === "0.00" ? "success" : "warning"} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table></TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Mayor General */}
        {tab === 3 && !loading && (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cuenta</InputLabel>
                  <Select value={cuentaSeleccionada} label="Cuenta" onChange={e => setCuentaSeleccionada(e.target.value)}>
                    {cuentas.map(c => (<MenuItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button variant="contained" onClick={cargarMayor} fullWidth disabled={!cuentaSeleccionada}>Consultar</Button>
              </Grid>
              <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "center" }}>
                <BotonesExportar onPDF={() => exportMayor("pdf")} onExcel={() => exportMayor("excel")} disabled={!mayor} />
              </Grid>
            </Grid>
            {mayor && (
              <Box>
                <Typography variant="subtitle1" gutterBottom><strong>{mayor.cuenta.codigo} - {mayor.cuenta.nombre}</strong></Typography>
                <TableContainer><Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Fecha</strong></TableCell><TableCell><strong>Detalle</strong></TableCell>
                    <TableCell align="right"><strong>Debe</strong></TableCell><TableCell align="right"><strong>Haber</strong></TableCell>
                    <TableCell align="right"><strong>Saldo</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {mayor.movimientos.map((m, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{m.fecha}</TableCell><TableCell>{m.glosa}</TableCell>
                        <TableCell align="right">{parseFloat(m.debe) > 0 ? `$${fmt(m.debe)}` : ""}</TableCell>
                        <TableCell align="right">{parseFloat(m.haber) > 0 ? `$${fmt(m.haber)}` : ""}</TableCell>
                        <TableCell align="right"><strong>${fmt(m.saldo)}</strong></TableCell>
                      </TableRow>
                    ))}
                    {mayor.movimientos.length === 0 && <TableRow><TableCell colSpan={5} align="center">Sin movimientos</TableCell></TableRow>}
                  </TableBody>
                </Table></TableContainer>
                <Alert severity="info" sx={{ mt: 1 }}>Saldo Final: <strong>${fmt(mayor.saldoFinal)}</strong></Alert>
              </Box>
            )}
          </Box>
        )}

        {/* Cuentas por Pagar */}
        {tab === 4 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarCxP} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar Reporte CxP</Button>
            <BotonesExportar onPDF={() => exportCxP("pdf")} onExcel={() => exportCxP("excel")} disabled={!reporteCxP} />
            {reporteCxP && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}><Alert severity="info">Total Comprado: <strong>${fmt(reporteCxP.resumen.total_comprado)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="success">Total Pagado: <strong>${fmt(reporteCxP.resumen.total_pagado)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="warning">Pendiente: <strong>${fmt(reporteCxP.resumen.total_pendiente)}</strong></Alert></Grid>
                </Grid>
                <TableContainer><Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Proveedor</strong></TableCell><TableCell><strong>RUC</strong></TableCell>
                    <TableCell><strong>Factura</strong></TableCell><TableCell><strong>Fecha</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell><TableCell align="right"><strong>Pagado</strong></TableCell>
                    <TableCell align="right"><strong>Saldo</strong></TableCell><TableCell><strong>Estado</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {reporteCxP.detalle.map((d, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{d.proveedor}</TableCell><TableCell>{d.ruc}</TableCell>
                        <TableCell>{d.numero}</TableCell><TableCell>{d.fecha?.split("T")[0]}</TableCell>
                        <TableCell align="right">${fmt(d.total)}</TableCell>
                        <TableCell align="right">${fmt(d.pagado)}</TableCell>
                        <TableCell align="right"><strong>${fmt(d.saldo)}</strong></TableCell>
                        <TableCell><Chip label={d.estado} size="small" color={d.estado === "PAGADA" ? "success" : d.estado === "PARCIAL" ? "warning" : "error"} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Cuentas por Cobrar */}
        {tab === 5 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarCxC} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar Reporte CxC</Button>
            <BotonesExportar onPDF={() => exportCxC("pdf")} onExcel={() => exportCxC("excel")} disabled={!reporteCxC} />
            {reporteCxC && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}><Alert severity="info">Total Facturado: <strong>${fmt(reporteCxC.resumen.total_facturado)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="success">Total Cobrado: <strong>${fmt(reporteCxC.resumen.total_cobrado)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="warning">Pendiente: <strong>${fmt(reporteCxC.resumen.total_pendiente)}</strong></Alert></Grid>
                </Grid>
                <TableContainer><Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Cliente</strong></TableCell><TableCell><strong>RUC</strong></TableCell>
                    <TableCell><strong>Secuencial</strong></TableCell><TableCell><strong>Fecha</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell><TableCell align="right"><strong>Cobrado</strong></TableCell>
                    <TableCell align="right"><strong>Saldo</strong></TableCell><TableCell><strong>Estado</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {reporteCxC.detalle.map((d, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{d.cliente}</TableCell><TableCell>{d.ruc}</TableCell>
                        <TableCell>{d.secuencial}</TableCell><TableCell>{d.fecha}</TableCell>
                        <TableCell align="right">${fmt(d.total)}</TableCell>
                        <TableCell align="right">${fmt(d.cobrado)}</TableCell>
                        <TableCell align="right"><strong>${fmt(d.saldo)}</strong></TableCell>
                        <TableCell><Chip label={d.estado} size="small" color={d.estado === "COBRADA" ? "success" : d.estado === "PARCIAL" ? "warning" : "error"} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Notas de Crédito */}
        {tab === 6 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarNC} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar Reporte NC</Button>
            <BotonesExportar onPDF={() => exportNC("pdf")} onExcel={() => exportNC("excel")} disabled={!reporteNC} />
            {reporteNC && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}><Alert severity="info">Total NC: <strong>${fmt(reporteNC.resumen.total)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="success">Autorizadas: <strong>{reporteNC.resumen.autorizadas}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="warning">Pendientes: <strong>{reporteNC.resumen.pendientes}</strong></Alert></Grid>
                </Grid>
                <TableContainer><Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Número</strong></TableCell><TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell><TableCell><strong>Factura Orig.</strong></TableCell>
                    <TableCell><strong>Motivo</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell><TableCell><strong>Estado SRI</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {reporteNC.detalle.map((d, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{d.numero}</TableCell><TableCell>{d.fecha}</TableCell>
                        <TableCell>{d.cliente}</TableCell><TableCell>{d.factura_original}</TableCell>
                        <TableCell>{d.motivo}</TableCell>
                        <TableCell align="right"><strong>${fmt(d.total)}</strong></TableCell>
                        <TableCell><Chip label={d.estado_sri} size="small" color={d.estado_sri === "AUTORIZADO" ? "success" : d.estado_sri === "PENDIENTE" ? "warning" : "error"} /></TableCell>
                      </TableRow>
                    ))}
                    {(!reporteNC.detalle || reporteNC.detalle.length === 0) && <TableRow><TableCell colSpan={7} align="center">Sin notas de crédito</TableCell></TableRow>}
                  </TableBody>
                </Table></TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Liquidaciones de Compras */}
        {tab === 7 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarLC} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar Reporte Liquidaciones</Button>
            <BotonesExportar onPDF={() => exportLC("pdf")} onExcel={() => exportLC("excel")} disabled={!reporteLC} />
            {reporteLC && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}><Alert severity="info">Total Comprado: <strong>${fmt(reporteLC.resumen.total)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="warning">Total Retenido: <strong>${fmt(reporteLC.resumen.total_retenido)}</strong></Alert></Grid>
                  <Grid item xs={4}><Alert severity="success">Total Pagado: <strong>${fmt(reporteLC.resumen.total_pagado)}</strong></Alert></Grid>
                </Grid>
                <TableContainer><Table size="small">
                  <TableHead><TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell><strong>Número</strong></TableCell><TableCell><strong>Fecha</strong></TableCell>
                    <TableCell><strong>Proveedor</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell><TableCell align="right"><strong>Retenido</strong></TableCell>
                    <TableCell align="right"><strong>A Pagar</strong></TableCell><TableCell><strong>Estado SRI</strong></TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {reporteLC.detalle.map((d, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{d.numero}</TableCell><TableCell>{d.fecha}</TableCell>
                        <TableCell>{d.proveedor}</TableCell>
                        <TableCell align="right">${fmt(d.total)}</TableCell>
                        <TableCell align="right">${fmt(d.retenido)}</TableCell>
                        <TableCell align="right"><strong>${fmt(d.a_pagar)}</strong></TableCell>
                        <TableCell><Chip label={d.estado_sri} size="small" color={d.estado_sri === "AUTORIZADO" ? "success" : d.estado_sri === "PENDIENTE" ? "warning" : "error"} /></TableCell>
                      </TableRow>
                    ))}
                    {(!reporteLC.detalle || reporteLC.detalle.length === 0) && <TableRow><TableCell colSpan={7} align="center">Sin liquidaciones</TableCell></TableRow>}
                  </TableBody>
                </Table></TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Asientos Contables */}
        {tab === 8 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarAsientos} startIcon={<Refresh />} sx={{ mb: 2 }}>
              Cargar Asientos Contables
            </Button>
            <BotonesExportar 
              onPDF={() => exportAsientos("pdf")} 
              onExcel={() => exportAsientos("excel")} 
              disabled={!reporteAsientos} 
            />
            
            {reporteAsientos && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Total de asientos encontrados: <strong>{reporteAsientos.length}</strong>
                </Alert>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell><strong>Fecha</strong></TableCell>
                        <TableCell><strong>Glosa</strong></TableCell>
                        <TableCell><strong>Comprobante</strong></TableCell>
                        <TableCell><strong>Tipo</strong></TableCell>
                        <TableCell><strong>Cuenta</strong></TableCell>
                        <TableCell align="right"><strong>Debe</strong></TableCell>
                        <TableCell align="right"><strong>Haber</strong></TableCell>
                        <TableCell><strong>Estado</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reporteAsientos.map((asiento) => {
                        if (asiento.detalles && asiento.detalles.length > 0) {
                          return asiento.detalles.map((detalle, detalleIndex) => {
                            const cuenta = cuentas.find(c => c.id === detalle.cuenta_id);
                            return (
                              <TableRow key={`${asiento.id}-${detalleIndex}`} hover>
                                <TableCell>
                                  {new Date(asiento.fecha_transaccion).toLocaleDateString()}
                                </TableCell>
                                <TableCell>{asiento.glosa}</TableCell>
                                <TableCell>{asiento.num_comprobante_sri || '—'}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={asiento.tipo_comprobante_sri || 'MANUAL'} 
                                    size="small" 
                                    color={
                                      asiento.tipo_comprobante_sri === '01' ? 'primary' :
                                      asiento.tipo_comprobante_sri === '03' ? 'secondary' :
                                      asiento.tipo_comprobante_sri === 'COMPRA' ? 'info' : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                    {cuenta?.codigo || '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {cuenta?.nombre || 'Cuenta no encontrada'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ color: parseFloat(detalle.debe || 0) > 0 ? '#d32f2f' : 'inherit' }}>
                                  {parseFloat(detalle.debe || 0) > 0 ? `$${fmt(detalle.debe)}` : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: parseFloat(detalle.haber || 0) > 0 ? '#2e7d32' : 'inherit' }}>
                                  {parseFloat(detalle.haber || 0) > 0 ? `$${fmt(detalle.haber)}` : '—'}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={asiento.estado} 
                                    size="small" 
                                    color={
                                      asiento.estado === 'CONTABILIZADO' ? 'success' :
                                      asiento.estado === 'BORRADOR' ? 'warning' : 'error'
                                    }
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          });
                        } else {
                          return (
                            <TableRow key={asiento.id} hover>
                              <TableCell>{new Date(asiento.fecha_transaccion).toLocaleDateString()}</TableCell>
                              <TableCell>{asiento.glosa}</TableCell>
                              <TableCell>{asiento.num_comprobante_sri || '—'}</TableCell>
                              <TableCell>
                                <Chip label={asiento.tipo_comprobante_sri || 'MANUAL'} size="small" />
                              </TableCell>
                              <TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Sin detalles
                              </TableCell>
                              <TableCell>
                                <Chip label={asiento.estado} size="small" color="warning" />
                              </TableCell>
                            </TableRow>
                          );
                        }
                      })}
                      {(!reporteAsientos || reporteAsientos.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4, fontStyle: 'italic', color: 'text.secondary' }}>
                            No hay asientos contables en el período seleccionado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Resumen Tributario */}
        {tab === 9 && !loading && (
          <Box>
            <Button variant="contained" onClick={cargarTributario} startIcon={<Refresh />} sx={{ mb: 2 }}>Generar Resumen Tributario</Button>
            <BotonesExportar onPDF={() => exportTributario("pdf")} onExcel={() => exportTributario("excel")} disabled={!reporteTrib} />
            {reporteTrib && (
              <Box>
                <Typography variant="h6" gutterBottom>Resumen de Obligaciones Tributarias</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">IVA</Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell>IVA en Ventas (cobrado)</TableCell><TableCell align="right"><strong>${fmt(reporteTrib.iva_ventas)}</strong></TableCell></TableRow>
                          <TableRow><TableCell>IVA en Compras (pagado)</TableCell><TableCell align="right"><strong>${fmt(reporteTrib.iva_compras)}</strong></TableCell></TableRow>
                          <TableRow sx={{ bgcolor: parseFloat(reporteTrib.iva_a_pagar) > 0 ? "#ffebee" : "#e8f5e9" }}>
                            <TableCell><strong>{parseFloat(reporteTrib.iva_a_pagar) > 0 ? "IVA a Pagar" : "Crédito Tributario IVA"}</strong></TableCell>
                            <TableCell align="right"><strong>${fmt(Math.abs(parseFloat(reporteTrib.iva_a_pagar)))}</strong></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" color="textSecondary">Retenciones</Typography>
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell>Ret. Fuente por Pagar (IR)</TableCell><TableCell align="right"><strong>${fmt(reporteTrib.retenciones_fuente_por_pagar)}</strong></TableCell></TableRow>
                          <TableRow><TableCell>Ret. IVA por Pagar</TableCell><TableCell align="right"><strong>${fmt(reporteTrib.retenciones_iva_por_pagar)}</strong></TableCell></TableRow>
                          <TableRow><TableCell>Crédito Tributario Renta</TableCell><TableCell align="right"><strong>${fmt(reporteTrib.credito_tributario_renta)}</strong></TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity={parseFloat(reporteTrib.total_obligaciones_sri) > 0 ? "warning" : "success"} sx={{ mt: 1 }}>
                      <strong>Total Obligaciones SRI del período: ${fmt(reporteTrib.total_obligaciones_sri)}</strong>
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
